import CryptoJS from 'crypto-js'
/**
 * Criptografa dados usando AES-256 no modo CBC com HMAC-SHA256 para integridade.
 *
 * <p>Processo:
 * <ol>
 *   <li>Serializa o dado para JSON, garantindo suporte a qualquer tipo de entrada.</li>
 *   <li>Gera a chave final de 256 bits aplicando SHA256 sobre a chave fornecida.</li>
 *   <li>Cria um IV (Initialization Vector) aleatório de 128 bits para a criptografia.</li>
 *   <li>Criptografa o dado usando AES-CBC com padding PKCS7.</li>
 *   <li>Calcula um HMAC-SHA256 concatenando IV e o texto cifrado para autenticação.</li>
 *   <li>Concatena HMAC + IV + texto cifrado e converte para Base64 para transporte seguro.</li>
 * </ol>
 *
 * @function encryptWithKey
 * @param {*} data - Dados a serem criptografados. Pode ser qualquer tipo (objeto, string, número, etc.).
 * @param {string} key - Chave secreta usada para derivar a chave AES-256.
 * @returns {string} String Base64 contendo HMAC, IV e o texto cifrado.
 * @throws {Error} Lança erro se houver falha na serialização, derivação de chave ou criptografia.
 * @author Lucas Fialho
 *
 * @example
 * const payload = { id: 123, name: "Alice" };
 * const secret = "minha-chave-secreta";
 * const encrypted = encryptWithKey(payload, secret);
 * console.log(encrypted);
 */
export function encryptWithKey(data, key) {
    try {
        // Serializar os dados
        const serialized = JSON.stringify(data);

        // Converter a chave para WordArray
        const keyHash = CryptoJS.enc.Utf8.parse(key);
        const keyHashSha256 = CryptoJS.SHA256(keyHash).toString(CryptoJS.enc.Hex);
        const finalKey = CryptoJS.enc.Hex.parse(keyHashSha256);

        // Gerar IV (16 bytes)
        const iv = CryptoJS.lib.WordArray.random(128 / 8);

        // Criptografar
        const encrypted = CryptoJS.AES.encrypt(serialized, finalKey, {
            iv: iv,
            mode: CryptoJS.mode.CBC,
            padding: CryptoJS.pad.Pkcs7
        });

        // Preparar dados para HMAC (IV + ciphertext)
        const ivAndCiphertext = iv.clone().concat(encrypted.ciphertext);

        // Calcular HMAC
        const hmac = CryptoJS.HmacSHA256(ivAndCiphertext, finalKey);

        // Concatenar HMAC + IV + ciphertext
        const result = hmac.concat(iv).concat(encrypted.ciphertext);

        // Converter para Base64
        return result.toString(CryptoJS.enc.Base64);
    } catch (error) {
        console.error('Encryption error:', error);
        throw error;
    }
}


/**
 * Descriptografa dados cifrados com AES-256 (modo CBC) e verifica integridade via HMAC-SHA256.
 *
 * <p>Processo:
 * <ol>
 *   <li>Decodifica o texto Base64 para bytes (WordArray).</li>
 *   <li>Extrai HMAC, IV (Initialization Vector) e o texto cifrado, com base nos tamanhos fixos.</li>
 *   <li>Gera a chave final de 256 bits aplicando SHA256 sobre a chave fornecida (mesmo processo da criptografia).</li>
 *   <li>Recalcula o HMAC do IV + texto cifrado e compara com o recebido para validar integridade.</li>
 *   <li>Se o HMAC for válido, descriptografa o conteúdo usando AES-CBC com padding PKCS7.</li>
 *   <li>Tenta interpretar o resultado como JSON; se falhar, retorna a string original.</li>
 * </ol>
 *
 * @function decryptWithKey
 * @param {string} encryptedData - Dados criptografados no formato Base64 (HMAC + IV + texto cifrado).
 * @param {string} key - Chave secreta usada na criptografia e derivada para AES-256.
 * @returns {*} Dados originais descriptografados. Pode ser um objeto, string ou outro tipo.
 * @throws {Error} Lança erro se:
 * - A verificação de integridade via HMAC falhar (dados possivelmente adulterados).
 * - Houver falha na descriptografia ou na decodificação do conteúdo.
 *
 * @author Lucas Fialho
 * @example
 * const secret = "minha-chave-secreta";
 * const encrypted = "BASE64..."; // Obtido via encryptWithKey
 * const originalData = decryptWithKey(encrypted, secret);
 * console.log(originalData);
 */
export function decryptWithKey(encryptedData, key) {
    try {
        // Converter de Base64 para WordArray
        const encryptedBytes = CryptoJS.enc.Base64.parse(encryptedData);

        // Tamanhos fixos (em bytes)
        const hmacSize = 256 / 8;  // HMAC-SHA256 = 32 bytes
        const ivSize = 128 / 8;    // IV AES = 16 bytes

        // Extrair componentes
        const hmacReceived = CryptoJS.lib.WordArray.create(
            encryptedBytes.words.slice(0, hmacSize / 4),
            hmacSize
        );

        const iv = CryptoJS.lib.WordArray.create(
            encryptedBytes.words.slice(hmacSize / 4, (hmacSize + ivSize) / 4),
            ivSize
        );

        const ciphertext = CryptoJS.lib.WordArray.create(
            encryptedBytes.words.slice((hmacSize + ivSize) / 4),
            encryptedBytes.sigBytes - hmacSize - ivSize
        );

        // Processar a chave (igual à encriptação)
        const keyHash = CryptoJS.enc.Utf8.parse(key);
        const keyHashSha256 = CryptoJS.SHA256(keyHash).toString(CryptoJS.enc.Hex);
        const finalKey = CryptoJS.enc.Hex.parse(keyHashSha256);

        // Verificar HMAC
        const ivAndCiphertext = iv.clone().concat(ciphertext);
        const hmacCalculated = CryptoJS.HmacSHA256(ivAndCiphertext, finalKey);

        // Comparação segura de HMAC
        if (hmacReceived.toString() !== hmacCalculated.toString()) {
            console.error('HMAC Received:', hmacReceived.toString());
            console.error('HMAC Calculated:', hmacCalculated.toString());
            throw new Error('HMAC verification failed - possible tampering');
        }

        // Descriptografar
        const decrypted = CryptoJS.AES.decrypt(
            { ciphertext: ciphertext },
            finalKey,
            {
                iv: iv,
                mode: CryptoJS.mode.CBC,
                padding: CryptoJS.pad.Pkcs7
            }
        );

        // Converter para string UTF-8
        const decryptedStr = decrypted.toString(CryptoJS.enc.Utf8);

        // Tentar parsear como JSON
        try {
            return JSON.parse(decryptedStr);
        } catch {
            return decryptedStr;
        }
    } catch (error) {
        console.error('Decryption error:', error);
        throw error;
    }
}